import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowRight, CheckCircle, Star, Mail, Brain, Zap, TrendingUp, Shield, Clock } from "lucide-react";
import { ArcadeEmbed } from "@/components/ArcadeEmbed";
import { openCalendlyDemo } from "@/lib/analytics";

const Hero = () => {
  return (
    <section className="bg-hero-gradient text-white min-h-screen relative overflow-hidden flex items-center">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-white/[0.02]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 0)`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          
          {/* LEFT SIDE - Content */}
          <div className="space-y-8 lg:pr-8">
            {/* Descriptive Badge */}
            <div className="flex items-center">
              <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2">
                <Star className="w-4 h-4 mr-2 fill-current" />
                Built for mid-market & enterprise finance teams
              </Badge>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
                AP automation,
                <span className="text-accent block">engineered for Oracle</span>
                <span className="text-white/80 text-xl lg:text-2xl xl:text-3xl block mt-3 font-semibold">
                  end-to-end AP operations for complex finance teams on Oracle Fusion &amp; EBS
                </span>
              </h1>

              <div className="w-24 h-1 bg-accent rounded-full"></div>
            </div>

            {/* Value Proposition — trailing sentence and proof paragraph are
                desktop-only so the primary CTA lands in the first mobile viewport */}
            <p className="text-lg sm:text-xl lg:text-2xl text-white/90 leading-relaxed max-w-2xl">
              EZ Cloud automates <span className="text-accent font-semibold">Oracle AP operations</span> end-to-end —
              from supplier-email intake through coding, approval, and posting into Oracle Fusion and EBS using
              Oracle-supported integration methods.{' '}
              <span className="hidden sm:inline">
                The same prebuilt, EZ Cloud-maintained integration extends to NetSuite, SAP, Dynamics, and Yardi
                where you need it.
              </span>
            </p>

            {/* HACOV proof */}
            <p className="hidden sm:block text-base lg:text-lg text-white/80 leading-relaxed max-w-2xl">
              Housing Authority of Covington went from 7-day AP cycles on Yardi Voyager to 2-day cycles,
              with an 80% drop in invoice errors and ~$79,000 in annual labor savings.
              <Link to="/customers/hacov" className="text-accent hover:underline ml-1 font-medium">See the case study →</Link>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                variant="secondary"
                className="text-xl px-8 py-6 h-auto font-bold shadow-2xl hover:shadow-accent/30 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                onClick={() => openCalendlyDemo()}
              >
                Book an Oracle AP demo
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    size="lg"
                    variant="outline-on-dark"
                    className="text-xl px-8 py-6 h-auto font-semibold"
                  >
                    Watch 2-min overview
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl w-[90vw] h-[80vh] p-0">
                  <DialogHeader className="sr-only">
                    <DialogTitle>2-minute platform overview</DialogTitle>
                  </DialogHeader>
                  <div className="w-full h-full">
                    <ArcadeEmbed />
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-white/60 text-sm">
              30-minute call · No commitment · Live walkthrough against Oracle Fusion, EBS, or your ERP
              <span className="block mt-1">
                Prefer numbers first?{' '}
                <Link to="/resources/roi-calculator" className="text-accent hover:underline font-medium">
                  Calculate your ROI →
                </Link>
              </span>
            </p>
          </div>

          {/* RIGHT SIDE - Visual Process Dashboard */}
          <div className="relative lg:pl-8">
            {/* Main Dashboard Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8 relative overflow-hidden">
              
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white">The EZ Cloud AP flow</h2>
                <p className="text-white/70">Four stages, one platform</p>
              </div>

              {/* Process Flow Visualization */}
              <div className="space-y-6">
                
                {/* Step 1 - Email AI Prevention */}
                <div className="bg-accent/10 border border-accent/20 rounded-2xl p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full -mr-16 -mt-16"></div>
                  <div className="flex items-center space-x-4 relative">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-white">AI Email Prevention</h3>
                        <Badge className="bg-accent/20 text-accent text-xs">Up to 90%</Badge>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div className="bg-accent h-2 rounded-full w-[90%] animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2 - Processing */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                      <Brain className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white mb-1">Smart Processing</h3>
                      <p className="text-white/60 text-sm">Capture, classify, and extract invoice data automatically</p>
                    </div>
                  </div>
                </div>

                {/* Step 3 - Approval */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white mb-1">Auto Approval</h3>
                      <p className="text-white/60 text-sm">Route by your business rules, with real-time ERP sync</p>
                    </div>
                  </div>
                </div>

                {/* Step 4 - Payment */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white mb-1">Payment Processing</h3>
                      <p className="text-white/60 text-sm">Execute payments with complete audit trail and compliance</p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Real case study stats — attributed so they're clearly not a live dashboard */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-white/10">
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">71%</div>
                  <div className="text-white/60 text-xs">Faster processing</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">80%</div>
                  <div className="text-white/60 text-xs">Fewer errors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">$79K+</div>
                  <div className="text-white/60 text-xs">Annual savings</div>
                </div>
              </div>
              <p className="text-center text-white/50 text-xs mt-3">Results: Housing Authority of Covington</p>

            </div>

            {/* Floating Feature Cards */}
            <div className="absolute -top-6 -right-6 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 shadow-xl">
              <div className="flex items-center space-x-3">
                <Shield className="w-6 h-6 text-accent" />
                <div>
                  <div className="font-semibold text-white text-sm">Enterprise Security</div>
                  <div className="text-white/60 text-xs">SOC 2 Compliant</div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 shadow-xl">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-6 h-6 text-green-400" />
                <div>
                  <div className="font-semibold text-white text-sm">Real-time Analytics</div>
                  <div className="text-white/60 text-xs">Live Dashboards</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;